import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquareIcon, 
  XIcon, 
  CheckIcon, 
  XCircleIcon,
  BellIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ChatWidget from "@/components/ChatWidget";
import { getApiEndpoint } from "@/lib/api";
import { getToken } from "@/lib/auth-storage";

interface ChatRequest {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  initial_message: string;
  status: string;
  created_at: string;
}

interface ChatNotificationsProps {
  currentUserId: number;
}

const ChatNotifications = ({ currentUserId }: ChatNotificationsProps) => {
  const [pendingRequests, setPendingRequests] = useState<ChatRequest[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    checkPendingRequests();
    // Verificar a cada 3 segundos
    const interval = setInterval(checkPendingRequests, 3000);
    return () => clearInterval(interval);
  }, []);

  const checkPendingRequests = async () => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint("/api/chat/admin/status"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data.pending_requests || []);
        
        // Se tem chat ativo e ainda não está aberto, abrir automaticamente
        if (data.has_active_chat && data.active_chat && !activeChatId) {
          setActiveChatId(data.active_chat.id);
        }
        
        // Se o chat foi fechado, limpar o estado
        if (!data.has_active_chat && activeChatId) {
          setActiveChatId(null);
        }
      }
    } catch (error) {
      console.error("Erro ao verificar solicitações:", error);
    }
  };

  const handleAccept = async (requestId: number) => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/chat/requests/${requestId}/accept`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActiveChatId(data.chat.id);
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        toast({
          title: "Chat aceito",
          description: `Chat iniciado com ${data.request.user_name}`,
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Erro",
          description: errorData.error || "Não foi possível aceitar a solicitação",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível aceitar a solicitação",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (requestId: number) => {
    try {
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/chat/requests/${requestId}/reject`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        toast({
          title: "Solicitação rejeitada",
          description: "A solicitação foi rejeitada",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível rejeitar a solicitação",
        variant: "destructive",
      });
    }
  };

  if (pendingRequests.length === 0 && !activeChatId) {
    return null;
  }

  return (
    <>
      {/* Notificações de Solicitações Pendentes */}
      {pendingRequests.length > 0 && (
        <div className="fixed top-20 right-6 z-50 space-y-2 max-w-md">
          {pendingRequests.map((request) => (
            <Card key={request.id} className="shadow-lg border-l-4 border-l-blue-600">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <BellIcon className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-sm">Nova Solicitação de Chat</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{request.user_name}</p>
                    <p className="text-xs text-gray-500">{request.user_email}</p>
                  </div>
                  {request.initial_message && (
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      "{request.initial_message}"
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleAccept(request.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Aceitar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(request.id)}
                      className="flex-1"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Rejeitar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Widget de Chat */}
      {activeChatId && (
        <ChatWidget
          chatId={activeChatId}
          onClose={() => setActiveChatId(null)}
          onChatEnded={() => {
            setActiveChatId(null);
            // Forçar atualização do status
            checkPendingRequests();
          }}
        />
      )}
    </>
  );
};

export default ChatNotifications;



